import React from "react"
import type { SwiperRef } from "swiper/react"

import { Analytics, SharedInteractionEvents } from "@bounce/analytics"
import { Button, SliderNavigation } from "@bounce/web-components"

import SafeLink from "../../Generic/SafeLink/SafeLink"
import { InteractionEvents } from "@/libs/analytics/events"
import type { PageType } from "@/types"

import MapIcon from "@bounce/assets/icons/Map.svg"

export type StoreSliderControlsProps = {
  cta?: string
  searchHref: string
  name: string
  from: PageType
  hideSliderNavigation: boolean
  swiperRef: React.RefObject<SwiperRef>
  storeCount: number
}

const StoreSliderCTAButton = ({
  cta,
  searchHref,
  name,
  from,
}: {
  cta: string
  searchHref: string
  name: string
  from: PageType
}) => {
  const trackCTAClick = () => {
    Analytics.track(SharedInteractionEvents.CTAPressed, {
      cta: "StoreSlider",
      sectionName: name,
      from,
    })
  }

  return (
    <Button
      as={SafeLink}
      className="max-w-full flex-1 md:flex-none"
      href={searchHref}
      iconClassName="w-5"
      LeftIcon={MapIcon}
      variant="secondary"
      onClick={trackCTAClick}>
      {cta}
    </Button>
  )
}

const StoreSliderNavigationControls = ({
  swiperRef,
  storeCount,
  name,
}: {
  swiperRef: React.RefObject<SwiperRef>
  storeCount: number
  name: string
}) => {
  const handleNext = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideNext()
      Analytics.track(InteractionEvents.StoreSliderNextPressed, {
        sectionName: name,
      })
    }
  }

  const handlePrev = () => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev()
      Analytics.track(InteractionEvents.StoreSliderPreviousPressed, {
        sectionName: name,
      })
    }
  }

  return (
    <SliderNavigation
      className="ml-auto"
      onClickNext={handleNext}
      onClickPrev={handlePrev}
      sliderItemSize={storeCount}
    />
  )
}

export const StoreSliderControls = ({
  cta,
  searchHref,
  name,
  from,
  hideSliderNavigation,
  swiperRef,
  storeCount,
}: StoreSliderControlsProps) => {
  if (!cta && hideSliderNavigation) {
    return null
  }

  return (
    <div className="mt-8 flex flex-wrap-reverse justify-between gap-8 px-6 md:gap-x-20 lg:mt-12">
      {!!cta && <StoreSliderCTAButton cta={cta} searchHref={searchHref} name={name} from={from} />}
      {!hideSliderNavigation && (
        <StoreSliderNavigationControls swiperRef={swiperRef} storeCount={storeCount} name={name} />
      )}
    </div>
  )
}
